import React, { Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { QueryClientProvider } from '@tanstack/react-query';
// ReactQueryDevtools intentionally kept for debugging - uncomment usage below when needed
// import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { AuthProvider } from './contexts/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';
import PageLoadingFallback from './components/PageLoadingFallback';
import { lazyWithTiming } from './utils/lazyWithTiming';
import { queryClient } from './lib/queryClient';

// Pages
const LandingPage = lazyWithTiming(() => import('./pages/LandingPage'), 'LandingPage');
const RegistrationForm = lazyWithTiming(() => import('./pages/RegistrationForm'), 'RegistrationForm');
const DraftSaved = lazyWithTiming(() => import('./pages/DraftSaved'), 'DraftSaved');
const SubmissionThankYou = lazyWithTiming(() => import('./pages/SubmissionThankYou'), 'SubmissionThankYou');
const EmployeeLogin = lazyWithTiming(() => import('./pages/EmployeeLogin'), 'EmployeeLogin');
const ApplicationsList = lazyWithTiming(() => import('./pages/EmployeeDashboard'), 'ApplicationsList');
const ApplicationDetail = lazyWithTiming(() => import('./pages/ApplicationDetail'), 'ApplicationDetail');
const EmployeeManagement = lazyWithTiming(() => import('./pages/EmployeeManagement'), 'EmployeeManagement');
const CustomersDashboard = lazyWithTiming(() => import('./pages/CustomersDashboard'), 'CustomersDashboard');
const CustomerDetail = lazyWithTiming(() => import('./pages/CustomerDetail'), 'CustomerDetail');
const MailPage = lazyWithTiming(() => import('./pages/MailPage'), 'MailPage');
const ContractsPage = lazyWithTiming(() => import('./pages/ContractsPage'), 'ContractsPage');
const NotFound = lazyWithTiming(() => import('./pages/NotFound'), 'NotFound');

function App() {
  // Use environment variable for basename, default to empty string for development
  const basename = process.env.REACT_APP_BASENAME || '';

  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <Router basename={basename}>
          <div className="App">
            <Toaster
              position="top-right"
              toastOptions={{
                duration: 4000,
                style: {
                  background: '#363636',
                  color: '#fff',
                },
              }}
            />
            <Suspense fallback={<PageLoadingFallback />}>
              <Routes>
                {/* Public routes */}
                <Route path="/" element={<LandingPage />} />
                <Route path="/register" element={<RegistrationForm />} />
                <Route path="/draft-saved" element={<DraftSaved />} />
                <Route path="/thank-you" element={<SubmissionThankYou />} />
                <Route path="/login" element={<EmployeeLogin />} />

                {/* Protected employee routes */}
                <Route path="/dashboard" element={
                  <ProtectedRoute>
                    <Navigate to="/dashboard/applications" />
                  </ProtectedRoute>
                } />
                <Route path="/dashboard/applications" element={
                  <ProtectedRoute>
                    <ApplicationsList />
                  </ProtectedRoute>
                } />
                <Route path="/dashboard/applications/:id" element={
                  <ProtectedRoute>
                    <ApplicationDetail />
                  </ProtectedRoute>
                } />
                <Route path="/dashboard/customers" element={
                  <ProtectedRoute>
                    <CustomersDashboard />
                  </ProtectedRoute>
                } />
                <Route path="/dashboard/customers/:id" element={
                  <ProtectedRoute>
                    <CustomerDetail />
                  </ProtectedRoute>
                } />
                <Route path="/dashboard/mail" element={
                  <ProtectedRoute>
                    <MailPage />
                  </ProtectedRoute>
                } />
                <Route path="/dashboard/employees" element={
                  <ProtectedRoute requireAdmin>
                    <EmployeeManagement />
                  </ProtectedRoute>
                } />
                <Route path="/dashboard/contracts" element={
                  <ProtectedRoute requireAdmin>
                    <ContractsPage />
                  </ProtectedRoute>
                } />

                {/* 404 Catch-all route */}
                <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
          </div>
        </Router>
      </AuthProvider>
      {/* React Query DevTools - uncomment for debugging queries
      {process.env.NODE_ENV !== 'production' && (
        <ReactQueryDevtools initialIsOpen={false} position="bottom-right" />
      )}
      */}
    </QueryClientProvider>
  );
}

export default App;
